(() => {
  const selector = '.product-card__media img, .neptune-gallery__image-zoom img, .neptune-gallery__lightbox-canvas img, .category-tile__media img, .store-visit__gallery-item img, .neptune-cart__image img, .media-hero__media img, .travel-guide__hero-media img';
  const tracked = new WeakMap();
  const videoSelector = '.media-hero__media video, .store-visit__tour-video, .neptune-gallery__video-player video';
  const trackedVideos = new WeakMap();
  const animationSelector = '.neptune-skeleton, [aria-busy="true"] .product-card, .neptune-cart__form[aria-busy="true"] .neptune-cart__summary-row strong, .neptune-cart__form[aria-busy="true"] [data-cart-pending] .neptune-cart__item-controls > strong, [data-quick-add][aria-busy="true"] .product-card__cart-control';
  const animated = new WeakSet();
  const animationObserver = 'IntersectionObserver' in window ? new IntersectionObserver((entries) => {
    entries.forEach(({ target, isIntersecting }) => target.classList.toggle('is-skeleton-visible', isIntersecting));
  }) : null;
  const trackAnimation = (target) => {
    if (animated.has(target)) return;
    animated.add(target);
    if (animationObserver) animationObserver.observe(target);
    else target.classList.add('is-skeleton-visible');
  };
  const scanAnimations = (root) => {
    if (root.matches?.(animationSelector)) trackAnimation(root);
    root.querySelectorAll?.(animationSelector).forEach(trackAnimation);
  };
  const observer = 'IntersectionObserver' in window ? new IntersectionObserver((entries) => {
    entries.forEach(({ target, isIntersecting }) => target.classList.toggle('is-loading-visible', isIntersecting));
  }) : null;
  const prepare = (image) => {
    tracked.get(image)?.();
    if (!image.getAttribute('src') || image.complete) return;
    const surface = image.parentElement;
    let timer;
    let timeout;
    const finish = () => {
      clearTimeout(timer);
      clearTimeout(timeout);
      surface.classList.remove('is-media-loading', 'is-loading-visible');
      observer?.unobserve(surface);
      image.removeEventListener('load', finish);
      image.removeEventListener('error', finish);
      tracked.delete(image);
    };
    tracked.set(image, finish);
    image.addEventListener('load', finish, { once: true });
    image.addEventListener('error', finish, { once: true });
    timer = setTimeout(() => {
      if (image.complete || !image.isConnected) return finish();
      surface.classList.add('is-media-loading');
      if (observer) observer.observe(surface);
      else surface.classList.add('is-loading-visible');
      timeout = setTimeout(finish, 15000);
    }, 120);
  };
  const scan = (root) => {
    if (root.matches?.(selector)) prepare(root);
    root.querySelectorAll?.(selector).forEach(prepare);
    if (root.matches?.(videoSelector)) prepareVideo(root);
    root.querySelectorAll?.(videoSelector).forEach(prepareVideo);
  };
  const prepareVideo = (video) => {
    if (trackedVideos.has(video)) return;
    const surface = video.parentElement;
    let timer;
    let timeout;
    const finish = () => {
      clearTimeout(timer);
      clearTimeout(timeout);
      surface.classList.remove('is-media-loading', 'is-loading-visible');
      observer?.unobserve(surface);
    };
    const start = () => {
      finish();
      timer = setTimeout(() => {
        if (!video.isConnected || video.readyState >= 3 || video.paused) return;
        surface.classList.add('is-media-loading');
        if (observer) observer.observe(surface);
        else surface.classList.add('is-loading-visible');
        timeout = setTimeout(finish, 10000);
      }, 120);
    };
    const pendingEvents = ['loadstart', 'waiting'];
    const readyEvents = ['canplay', 'playing', 'pause', 'error', 'emptied'];
    pendingEvents.forEach((event) => video.addEventListener(event, start));
    readyEvents.forEach((event) => video.addEventListener(event, finish));
    trackedVideos.set(video, () => {
      finish();
      pendingEvents.forEach((event) => video.removeEventListener(event, start));
      readyEvents.forEach((event) => video.removeEventListener(event, finish));
      trackedVideos.delete(video);
    });
  };
  scan(document);
  scanAnimations(document);
  new MutationObserver((records) => {
    records.forEach((record) => {
      if (record.type === 'attributes') {
        if (['src', 'srcset'].includes(record.attributeName) && record.target.matches(selector)) prepare(record.target);
        else if (record.attributeName === 'aria-busy') {
          if (record.target.getAttribute('aria-busy') === 'true') scanAnimations(record.target);
          else record.target.querySelectorAll('.is-skeleton-visible').forEach((target) => {
            animationObserver?.unobserve(target);
            animated.delete(target);
            target.classList.remove('is-skeleton-visible');
          });
        }
      } else {
        record.addedNodes.forEach((node) => { if (node.nodeType === 1) { scan(node); scanAnimations(node); } });
        record.removedNodes.forEach((node) => {
          if (node.nodeType !== 1) return;
          if (node.matches(selector)) tracked.get(node)?.();
          node.querySelectorAll(selector).forEach((image) => tracked.get(image)?.());
          if (node.matches(videoSelector)) trackedVideos.get(node)?.();
          node.querySelectorAll(videoSelector).forEach((video) => trackedVideos.get(video)?.());
          [node, ...node.querySelectorAll('.is-skeleton-visible, .neptune-skeleton, .product-card, .product-card__cart-control, strong')].forEach((target) => { animationObserver?.unobserve(target); animated.delete(target); target.classList.remove('is-skeleton-visible'); });
        });
      }
    });
  }).observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['src', 'srcset', 'aria-busy'] });
  const visibility = () => document.documentElement.toggleAttribute('data-loading-paused', document.hidden);
  document.addEventListener('visibilitychange', visibility);
  visibility();
})();
